import { Component, ElementRef, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Account } from 'src/app/shared/data/models/account.model';
import { SharedService } from 'src/app/shared/data/shared.service';
import { environment } from 'src/environments/environment';
import { FormGroup, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { EMAIL_PATTERN, PHONE_REGEX } from 'src/app/shared/util/shared.util';
import { AccountService } from './data/account.service';
import { MarkdownPipe } from 'src/app/shared/data/pipes/markdown.pipe';
import { SelectComponent, SelectOption } from 'src/app/shared/ui-common/select/select.component';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from 'src/app/auth/data/auth.service';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';
import { FeaturePillMenuComponent } from 'src/app/shared/feature-pill-menu/feature-pill-menu.component';
import { PillComponent } from 'src/app/shared/ui-common/pill/pill.component';
import { ButtonComponent } from 'src/app/shared/ui-common/button/button.component';
import { TranslationService } from 'src/app/shared/data/translation.service';
import { HttpErrorResponse, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { AccountUpdate } from './data/account.model';

enum AccountItemEnum {
  Email = 'email',
  Password = 'password',
  Phone = 'phone'
}

@Component({
  selector: 'app-feature-account',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MarkdownPipe,
    SelectComponent,
    RouterModule,
    TranslatePipe,
    PillComponent,
    FeaturePillMenuComponent,
    ButtonComponent
  ],
  templateUrl: './feature-account.component.html',
  styleUrl: './feature-account.component.scss',
})
export class FeatureAccountComponent implements OnInit {
  readonly accountItemEnum = AccountItemEnum;
  s3BaseUrl = environment.s3BaseUrl;
  companyLogo = signal<string>('');
  account = signal<Account | null>(null);
  changePasswordResponse = signal<HttpResponse<AccountUpdate> | null>(null);
  changePasswordError = signal<HttpErrorResponse | null>(null);
  activeGridItem = signal<AccountItemEnum | null>(null);
  declarationContent = signal<'consent' | 'revoke' | ''>('');
  activeContent = signal<'form' | 'about' | 'declaration' | 'confirmation'>('form');
  accountForm: FormGroup;
  areAllPasswordsFilled = signal(false);
  declarationText = '';
  departmentOptions: SelectOption[] = [];
  pillExpanded = false;
  waitingForAction = false;
  changingPassword = false;

  @ViewChild('currentPassword') currentPassword!: ElementRef;
  @ViewChild('newPassword') newPassword!: ElementRef;
  @ViewChild('confirmPassword') confirmPassword!: ElementRef;
  @ViewChild('emailInput') emailInput!: ElementRef;
  @ViewChild('phoneInput') phoneInput!: ElementRef;

  get isHowdyLogo(): boolean {
    return this.companyLogo().includes('HRONWellbeing');
  }

  get title(): string {
    if (this.activeContent() === 'about') {
      return 'WebApp.v1.Account.AboutThisApp';
    } else if (this.activeContent() === 'declaration') {
      return 'WebApp.v1.Account.DeclarationOfConsent';
    }

    return 'WebApp.v1.Account.MyAccount';
  }

  get subTitle(): string {
    if (this.activeContent() === 'about') {
      return 'HR-ON Wellbeing';
    } else if (this.activeContent() === 'declaration') {
      return 'WebApp.v1.Account.ConsentToProcessing';
    }

    return this.account()?.firstname + ' ' + this.account()?.lastname;
  }

  get isPhoneControlInvalid(): boolean {
    return !!this.accountForm.get('phone')?.invalid && !!this.accountForm.get('phone')?.dirty ;
  }

  get isSsoAccount(): boolean {
    return this.account()?.isSsoAccount ?? false;
  }

  constructor(
    private formBuilder: FormBuilder,
    private sharedService: SharedService,
    private accountService: AccountService,
    private authService: AuthService,
    private location: Location,
    private router: Router,
    private translationService: TranslationService
  ) {
    this.accountForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(EMAIL_PATTERN)]],
      password: ['1234', [Validators.required]],
      phone: ['', [Validators.required, Validators.pattern(PHONE_REGEX)]],
      division: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.companyLogo.set(this.sharedService.getCompanyLogoUrl(true));
    this.sharedService.getTermsOfService().then(tos => {
      this.declarationText = tos;
    });

    this.sharedService.fetchAccountData().then((account) => {
      this.account.set(account);

      if (account) {
        this.accountForm.patchValue({
          email: account.email,
          phone: account.phonenumber,
          division: account.projects
        });

        if (account.department) {
          this.departmentOptions.push({
            label: account.department,
            value: account.department
          });
        }

        account.divisions.forEach(division => {
          this.departmentOptions.push({
            label: division,
            value: division
          });
        });

        account.projects.forEach(project => {
          this.departmentOptions.push({
            label: project,
            value: project
          });
        });
      }
    });
  }

  onGoBack(): void {
    this.location.back();
  }

  getHowdyLogo(): void {
    this.companyLogo.set(this.sharedService.getHowdyLogoUrl());
  }

  showButtons(activeItem: AccountItemEnum, isEnabled = true, isPassword = false): void {
    if (!isEnabled) {
      return;
    }

    if (isPassword) {
      setTimeout(() => this.currentPassword?.nativeElement.focus(), 50);
    }

    this.activeGridItem.set(activeItem);
  }

  updatePlaceholder(
    element: ElementRef,
    updatedPlaceholder: string,
    originalPlaceholder: string,
    value: string,
    status: HttpStatusCode
  ): void {
    const color = status === HttpStatusCode.BadRequest
      ? 'error'
      : 'text-green';

    element.nativeElement.value = null;
    element.nativeElement.classList.add(color);
    element.nativeElement.placeholder = updatedPlaceholder;

    setTimeout(() => {
      element.nativeElement.value = value;
      element.nativeElement.placeholder = originalPlaceholder;
      element.nativeElement.classList.remove(color);
    }, 2000);
  }

  resetEmailField(): void {
    this.emailInput.nativeElement.value = this.account()?.email ?? '';
    this.emailInput.nativeElement.classList.remove('error');
    this.emailInput.nativeElement.placeholder = this.translationService.translate('WebApp.v1.Account.Email');
    this.activeGridItem.set(null);
  }

  changeEmail(): void {
    const emailValid = this.accountForm.get('email')?.valid;
    const email = this.accountForm.get('email')?.value;

    if (!emailValid) {
      this.emailInput.nativeElement.value = null;
      this.emailInput.nativeElement.classList.add('error');
      this.emailInput.nativeElement.placeholder = 'WebApp.v1.Account.Error.InvalidEmail';
    }

    this.activeGridItem.set(null);
    this.accountService.updateAccount({ NewEmail: email }).subscribe({
      next: response => {
        if (response.status === 200) {
          if (this.account()) {
            const updatedAccount = {
              ...this.account(),
              email: email
            };
            this.account.set(updatedAccount as Account);
            this.emailInput.nativeElement.blur();
            this.updatePlaceholder(
              this.emailInput,
              this.translationService.translate('WebApp.v1.Account.EmailUpdated'),
              this.translationService.translate('WebApp.v1.Account.Email'),
              email,
              response.status
            );
          }
        }
      },
      error: error => {
        this.updatePlaceholder(
          this.emailInput,
          error.error,
          this.translationService.translate('WebApp.v1.Account.Email'),
          email,
          HttpStatusCode.BadRequest
        );
      }
    });
  }

  resetPhoneField(): void {
    this.phoneInput.nativeElement.value = this.account()?.phonenumber ?? '';
    this.phoneInput.nativeElement.classList.remove('error');
    this.phoneInput.nativeElement.placeholder = this.translationService.translate('WebApp.v1.Account.PhoneNumber');
    this.activeGridItem.set(null);
  }

  changePhoneNumber(): void {
    const phoneValid = this.accountForm.get('phone')?.valid;
    const phone = this.accountForm.get('phone')?.value;

    if (!phoneValid) {
      this.phoneInput.nativeElement.value = null;
      this.phoneInput.nativeElement.classList.add('error');
      this.phoneInput.nativeElement.placeholder = this.translationService.translate('WebApp.v1.Account.Error.InvalidPhoneNumber');

      return;
    }

    this.activeGridItem.set(null);
    this.accountService.updateAccount({ NewMobile: phone }).subscribe({
      next: response => {
        if (response.status === 200) {

          if (this.account()) {
            const updatedAccount = {
              ...this.account(),
              phonenumber: phone
            };
            this.account.set(updatedAccount as Account);
            this.phoneInput.nativeElement.blur();
            this.updatePlaceholder(
              this.phoneInput,
              this.translationService.translate('WebApp.v1.Account.PhoneNumberUpdated'),
              this.translationService.translate('WebApp.v1.Account.PhoneNumber'),
              phone,
              response.status
            );
          }
        }
      },
      error: error => {
        console.error('Error updating phone number:', error);
        this.updatePlaceholder(
          this.phoneInput,
          error.error,
          this.translationService.translate('WebApp.v1.Account.PhoneNumber'),
          phone,
          HttpStatusCode.BadRequest
        );
      }
    });
  }

  changePassword(): void {
    this.changingPassword = true;
    this.changePasswordError.set(null);
    const passwordsDontMatch = this.newPassword.nativeElement.value !== this.confirmPassword.nativeElement.value;

    if (passwordsDontMatch) {
      this.confirmPassword.nativeElement.value = null;
      this.confirmPassword.nativeElement.classList.add('error');
      this.confirmPassword.nativeElement.placeholder = this.translationService.translate('WebApp.v1.Account.Error.NewPasswordDoesntMatch');
      this.changingPassword = false;

      return;
    }

    const payload = {
      NewPIN: this.newPassword.nativeElement.value,
      OldPIN: this.currentPassword.nativeElement.value
    };

    this.accountService.updateAccount(payload).subscribe({
      next: response => {
        this.changePasswordError.set(null);
        this.changePasswordResponse.set(response);
        if (response.status === 200) {
          if (this.account()) {
            const updatedAccount = {
              ...this.account(),
              pin: this.newPassword.nativeElement.value
            };
            this.account.set(updatedAccount as Account);

            setTimeout(() => {
              this.resetPasswordFields();
            }, 2000);
          }
        }
      },
      error: (error: HttpErrorResponse) => {
        this.changingPassword = false;
        this.changePasswordResponse.set(null);
        this.changePasswordError.set(error);

        setTimeout(() => {
          this.resetPasswordFields();
        }, 2000);
      }
    });
  }

  rejectTermsOfService(): void {
    this.waitingForAction = true;
    this.sharedService.rejectTermsOfService().then(() => {
      this.activeContent.set('confirmation');
      this.waitingForAction = false;

      setTimeout(() => {
        this.activeContent.set('form');
      }, 2000);
    });
  }

  resendTermsOfService(): void {
    this.waitingForAction = true;
    this.accountService.resendTermsOfService().then(() => {
      this.activeContent.set('confirmation');
      this.waitingForAction = false;

      setTimeout(() => {
        this.activeContent.set('form');
      }, 2000);
    });
  }

  checkPasswords(): void {
    this.areAllPasswordsFilled.set(
      Boolean(this.currentPassword?.nativeElement?.value) &&
      Boolean(this.newPassword?.nativeElement?.value) &&
      Boolean(this.confirmPassword?.nativeElement?.value)
    );
  }

  showContent(content: 'about' | 'declaration'): void {
    this.activeContent.set(content);
  }

  backToForm(): void {
    this.activeContent.set('form');
  }

  resetPasswordFields(): void {
    this.activeGridItem.set(null);
    this.changePasswordError.set(null);
    this.changingPassword = false;
    this.changePasswordResponse.set(null);

    if (this.currentPassword?.nativeElement) {
      this.currentPassword.nativeElement.value = null;
    }
    if (this.newPassword?.nativeElement) {
      this.newPassword.nativeElement.value = null;
    }
    if (this.confirmPassword?.nativeElement) {
      this.confirmPassword.nativeElement.value = null;
    }
    this.areAllPasswordsFilled.set(false);
  }

  onLogout(): void {
    this.authService.logout();
  }

  goToMenu(): void {
    this.router.navigate(['/available-surveys']);
  }
}
